import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { Distribution } from '../../shared/models/domain/distribution';
import { StaticValuesUtils } from '../../utils/static-values/static-values.utils';
import { SearchResponse } from 'src/app/shared/models/domain/search-response';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    apiURL: string = '/s-transfers/api';
    distribution: { adults; children };
    private serviceId: string;
    private queryParamSubscription: Subscription;

    constructor(
        private httpClient: HttpClient,
        private appSettings: AppSettingsService,
        private activatedRoute: ActivatedRoute
    ) {
        this.queryParamSubscription = this.activatedRoute.queryParamMap.subscribe(params => {
            this.serviceId = params.get('serviceId');
        });
    }

    public getSearchResponse(routeParams: any): Observable<SearchResponse> {
        const headers = new HttpHeaders({
            'X-Tracking': 'page-view',
        });

        const params = this.getSearchParams(routeParams);

        return this.httpClient.get<SearchResponse>(`${this.apiURL}/transfers`, {
            headers,
            params,
        });
    }

    public getCurrentDistribution(): Distribution {
        return this.distribution;
    }

    private getSearchParams(routeParams: any): HttpParams {
        this.distribution = this.getDistributionParams(routeParams.params.distribution);
        const formatedFromDate = this.getFormattedDate(routeParams.params.fromDate);
        const originType = this.getRoutePointType(routeParams.params.originType);
        const destinationType = this.getRoutePointType(routeParams.params.destinationType);
        const serviceId = this.getServiceId();
        const coupon_filter = StaticValuesUtils.getCouponFilter();
        const promotions = StaticValuesUtils.getPromotions();

        let params = new HttpParams()
            .set('site', this.appSettings.getSite())
            .set('language', this.appSettings.getLanguage())
            .set('sorting', this.appSettings.getSorting())
            .set('channel', this.appSettings.getChannel())
            .set('adults', this.distribution.adults)
            .set('children_ages', this.distribution.children)
            .set('origin_type', originType)
            .set('origin', routeParams.params.originCode)
            .set('destination_type', destinationType)
            .set('destination', routeParams.params.destinationCode)
            .set('options', this.getAdditionalsFields().join(','))
            .set('pageview_id', StaticValuesUtils.getPageViewId())
            .set('from_date', `${formatedFromDate} ${routeParams.params.fromTime}`);

        //TODO check roundtrip by route?
        if (routeParams.params.toDate) {
            const formatedToDate = this.getFormattedDate(routeParams.params.toDate);

            // TODO merge params objects?
            params = params.set('to_date', `${formatedToDate} ${routeParams.params.toTime}`);
            params = params.set('return_destination', routeParams.params.originCode);
            params = params.set('return_destination_type', originType);
            params = params.set('return_origin', routeParams.params.destinationCode);
            params = params.set('return_origin_type', destinationType);
        }

        if (serviceId) {
            params = params.set('service_id', serviceId);
        }

        if (coupon_filter) {
            params = params.set('coupon_filter', coupon_filter);
        }

        if (promotions) {
            params = params.set('promotions', promotions);
        }

        if (routeParams.params.currencies) {
            params = params.set('currencies', routeParams.params.currencies);
        }

        if (routeParams.params.currency) {
           params = params.set('currency', routeParams.params.currency);
        }

        return params;
    }

    private getFormattedDate(dateParam: string): string {
        // convert 31-12-2019 to 2019-12-31 (api expects YYYY-MM-DD)
        return dateParam.split('-').reverse().join('-');
    }

    private getRoutePointType(routePointType: string): string {
        // TODO revisar api wtf
        // api expects HOTEL or AIRPORT or PLACE
        return routePointType.toUpperCase().replace('GEO_', '');
    }

    public getDistributionParams(distribution: string): {
        adults: string;
        children: string;
    } {
        // distribution 3-4-5 means 3 adults & 2 children with ages 4 and 5
        const adults = distribution.split('-')[0];

        let children = distribution.substring(adults.length + 1); // remove adults and dash
        if (children) {
            // api expects comma separated children ages
            children = children.replace(/-/g, ',');
        }

        return {
            adults,
            children,
        };
    }

    private getAdditionalsFields() {
        return ['maximumPaymentPlan', 'cancellation_policies'];
    }

    private getServiceId(): string {
        return this.serviceId;
    }

    public cleanUp(): void {
        if (this.queryParamSubscription) {
            this.queryParamSubscription.unsubscribe();
        }
    }
}
